import { useState, useRef, useEffect, useCallback } from "react";
import { IconBell } from "@tabler/icons-react";
import { IoIosMore } from "react-icons/io";
import { useAppDispatch, useAppSelector } from "../../../store";
import { INotification } from "../../../store/@types/notifications";
import {
  getNotifications,
  seenNotification,
} from "../../../service/api/notification";
import { NotificationAccordionItem } from "./NotificationItem";

export function NotificationPanel() {
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState<"all" | "unread">("all");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<INotification | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);

  // Always fallback safely — FIXES undefined errors
  const notifications =
    useAppSelector(({ notification }) => notification?.notifications) || [];

  const onLoad = useCallback(() => {
    getNotifications(dispatch);
  }, [dispatch]);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  // Close panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Mark as seen only once
  const handleMarkAsSeen = (notification: INotification) => {
    if (notification.seen) return;
    seenNotification(dispatch, notification.id.toString());
  };

  const unreadCount = notifications.filter(
    (n: INotification) => !n.seen,
  ).length;

  const displayedNotifications =
    activeTab === "unread"
      ? notifications.filter((n: INotification) => !n.seen)
      : notifications;

  const newNotifications = displayedNotifications.slice(0, 6);
  const earlierNotifications = displayedNotifications.slice(6);

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-9 h-9 grid place-content-center bg-primary-50/30 rounded-lg text-primary-900 cursor-pointer mr-2"
      >
        <IconBell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] rounded-full px-1.5 py-[1px]">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-[360px] rounded-lg border border-gray-50/10 shadow-md z-50 bg-white">
          <div className="flex items-center justify-between p-4 pb-2">
            <h2 className="text-2xl font-bold">Notifications</h2>
            <button className="h-8 w-8 rounded-md hover:bg-accent hover:text-accent-foreground flex items-center justify-center">
              <IoIosMore className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="px-4 pb-2">
            <div className="inline-flex h-10 w-full items-center bg-gray-50/10 p-1 rounded-md">
              <button
                onClick={() => setActiveTab("all")}
                className={`flex-1 px-3 py-1.5 text-sm rounded-sm ${
                  activeTab === "all" ? "bg-white shadow-sm" : ""
                }`}
              >
                All
              </button>

              <button
                onClick={() => setActiveTab("unread")}
                className={`flex-1 px-3 py-1.5 text-sm rounded-sm ${
                  activeTab === "unread" ? "bg-white shadow-sm" : ""
                }`}
              >
                Unread
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="h-[500px] overflow-y-auto">
            {newNotifications.length === 0 && (
              <div className="flex items-center justify-center h-40 text-gray-500">
                No notifications
              </div>
            )}

            {newNotifications.map((notification: INotification) => (
              <NotificationAccordionItem
                key={notification.id}
                notification={notification}
                isExpanded={selectedNotification?.id === notification.id}
                onToggle={() =>
                  setSelectedNotification((prev) =>
                    prev?.id === notification.id ? null : notification,
                  )
                }
                onMarkAsSeen={handleMarkAsSeen}
              />
            ))}

            <div>
              {earlierNotifications.length > 0 && (
                <div className="px-4 pb-4">
                  <h3 className="font-bold text-lg mb-2">Earlier</h3>
                  {earlierNotifications.map((notification: INotification) => (
                    <NotificationAccordionItem
                      key={notification.id}
                      notification={notification}
                      isExpanded={selectedNotification?.id === notification.id}
                      onToggle={() =>
                        setSelectedNotification((prev) =>
                          prev?.id === notification.id ? null : notification,
                        )
                      }
                      onMarkAsSeen={handleMarkAsSeen}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
